import {
	Box,
	List
} from '@chakra-ui/react';

import { EstimateItem } from './estimate-item';
import { EstimateItemAddAccessories } from './estimate-item-add-accessories';
import { EstimateItemAddPromotionalSku } from './estimate-item-add-promotional-sku';
import { Kitchen, KitchenEstimateLineItem, KitchenItemizedMsrp } from '@rta/rta-blueprint-library';

export type EstimateItemizedContentProps = {
	kitchenData: Kitchen;
	itemData: KitchenItemizedMsrp | null;
}

// A custom override is one generic sku with a designer-typed description, so identity is the pair.
// The row shows the LINE total, so divide by qty to land back on the per-unit msrp the item stores.
const setCustomItemPrice = (kitchen: Kitchen, row: KitchenEstimateLineItem, price: number): void => {
	const each = row.qty > 0 ? price / row.qty : price;
	kitchen.design?.islands?.children.forEach((island) => island.panels.children.forEach((panel) => panel.panelItemGroups.children.forEach((group) => group.items.children.forEach((panelItem) => {
		const { item } = panelItem;
		if (item.override && item.fqSku === row.sku && item.description === row.description) item.msrp = each;
	}))));
};

export const EstimateItemizedContent = (props: EstimateItemizedContentProps): React.ReactElement => {
	const { kitchenData } = props;
	// Product rows come from the ONE canonical builder shared with the PDF and design package
	const estimateRows = kitchenData?.estimateLineItems ?? [];
	const rowsFor = (group: KitchenEstimateLineItem['group']): Array<KitchenEstimateLineItem> => estimateRows.filter((row) => row.group === group);
	const renderRows = (group: KitchenEstimateLineItem['group']): Array<React.ReactElement> => rowsFor(group).map((row) => (
		<EstimateItem
			key={`${row.sku}|${row.description}`}
			sku={row.sku}
			description={row.label}
			quantity={row.qty}
			price={row.price}
			currency={{ locale: 'en-US', currency: 'USD' }}
			warning={row.warning}
			discontinued={row.discontinued}
			{...(row.isCustomOverride ? { onPriceChange: (price: number) => setCustomItemPrice(kitchenData, row, price) } : {})}
		/>
	));

	const promotionalData = kitchenData?.promotionalSkus?.children ?? null;
	const discountData = promotionalData?.filter((pSku) => pSku.isDiscount) ?? [];
	const promotionData = promotionalData?.filter((pSku) => !pSku.isDiscount) ?? [];
	return (
		<Box pl="10px" pr="10px" overflowY={'scroll'}>
			Islands:
			<Box pb="0px" pt="5px" borderTop={'1px'} >
				{renderRows('island')}
			</Box>
			Countertops:
			<Box pb="0px" pt="5px" borderTop={'1px'} >
				{renderRows('countertop')}
			</Box>
			Backsplashes:
			<Box pb="0px" pt="5px" borderTop={'1px'}>
				{renderRows('backsplash')}
			</Box>
			Appliances:
			<Box pb="0px" pt="5px" borderTop={'1px'}>
				{renderRows('appliance')}
			</Box>
			Accessories:
			<Box pb="0px" pt="5px" borderTop={'1px'}>
				<List spacing={2}>
					{rowsFor('accessory').map((row) => (
						<EstimateItem
							key={row.sku}
							sku={row.sku}
							description={row.label}
							quantity={row.qty}
							price={row.price}
							currency={{ locale: 'en-US', currency: 'USD' }}
							isMutable={true}
							itemType={'accessory'}
							discontinued={row.discontinued}
						/>
					))}
				</List>
			</Box>
			<EstimateItemAddAccessories />
			{discountData.length > 0 && (<>
				Discounts:
				<Box pb="0px" pt="5px" borderTop={'1px'}>
					<List spacing={2}>
						{discountData.map((pSku) => {
							const { sku, description, hasDiscountRules, totalAdjustment, priceAdjustment, adjustmentType, discountRules, ruleBreakdown, quantity } = pSku;
							return (
								<EstimateItem
									key={sku}
									sku={sku}
									description={description}
									quantity={quantity ?? 1}
									price={hasDiscountRules ? totalAdjustment : priceAdjustment}
									totalAdjustment={totalAdjustment}
									currency={{ locale: 'en-US', currency: 'USD' }}
									type={hasDiscountRules ? undefined : adjustmentType}
									isMutable={true}
									isPriceMutable={!hasDiscountRules || discountRules.length === 1}
									itemType={'promotion'}
									discountBreakdown={hasDiscountRules ? discountRules.map((rule) => {
										const { target, priceAdjustment: rulePrice, adjustmentType: ruleType } = rule;
										return {
											label: ruleType === 'PERCENTAGE'
												? `${target} (${rulePrice}%)`
												: `${target} ($${rulePrice})`,
											amount: ruleBreakdown[target] ?? 0
										};
									}) : undefined}
								/>
							);
						})}
					</List>
				</Box>
			</>)}
			Promotions:
			<Box pb="0px" pt="5px" borderTop={'1px'}>
				<List spacing={2}>
					{promotionData.map((pSku) => {
						const { sku, description, hasDiscountRules, totalAdjustment, priceAdjustment, adjustmentType, discountRules, ruleBreakdown, quantity } = pSku;
						return (
							<EstimateItem
								key={sku}
								sku={sku}
								description={description}
								quantity={quantity ?? 1}
								price={hasDiscountRules ? totalAdjustment : priceAdjustment}
								totalAdjustment={totalAdjustment}
								currency={{ locale: 'en-US', currency: 'USD' }}
								type={hasDiscountRules ? undefined : adjustmentType}
								isMutable={true}
								isPriceMutable={!hasDiscountRules || discountRules.length === 1}
								itemType={'promotion'}
								discountBreakdown={hasDiscountRules ? discountRules.map((rule) => {
									const { target, priceAdjustment: rulePrice, adjustmentType: ruleType } = rule;
									return {
										label: ruleType === 'PERCENTAGE'
											? `${target} (${rulePrice}%)`
											: `${target} ($${rulePrice})`,
										amount: ruleBreakdown[target] ?? 0
									};
								}) : undefined}
							/>
						);
					})}
				</List>
			</Box>
			<EstimateItemAddPromotionalSku />
		</Box>

	);
};
